import React from "react";
import Bank from "assets/images/svg/bank.svg";
import { useFormattedMessage } from "hooks";
import { friendlyFormatIBAN } from "ibantools";
import CustomFormatNumber from "components/CustomFormatNumber";
import ExplanatoryTooltip from "components/ExplanatoryTooltip";

const fallback = {
    Owner: "------",
    IBAN: "------",
    BIC: "------",
};

const messages = {
    amount: "amount",
    accountOwner: "owner",
    country: "country_placeholder",
    ibanInfoText: "iban_info_text",
    verificationRequired: "verification_required",
    verifyYourBankAccount: "verify_your_bank_account",
    completeMobileVerificationMangopay: "complete_mobile_verification_mangopay",
    verify: "verify",
};

const AccountDetailsBlock = ({
    className = "",
    data = {},
    amount = 0,
    isVerificationRequired = false,
    verificationDesign = "large",
    onVerifyClick = () => {},
}) => {
    const { formatMessage } = useFormattedMessage();
    const newData = data || fallback;

    return (
        <div className={`card-container account-detail-block ${className}`}>
            <div className="d-flex align-start p-5">
                <div className="round-holder mr-4 bg-dark">
                    <Bank />
                </div>
                <div className="w-100 desktop-screen">
                    <span className="text-14 d-block mb-3">
                        <span className="another-grey-color d-inline-block mr-2 fw-500">
                            {formatMessage(messages.accountOwner)}
                        </span>
                        <span className="fw-600">{newData.Owner}</span>
                    </span>
                    <span className="text-14 d-block mb-3">
                        <span className="another-grey-color d-inline-flex mr-2 fw-500">
                            IBAN{" "}
                            <ExplanatoryTooltip dataTooltipID="iban" dataTooltipContent={formatMessage(messages.ibanInfoText)} />
                        </span>
                        <span className="fw-600 font-noto-serif">{friendlyFormatIBAN(newData.IBAN)}</span>
                    </span>
                    <div className="flex justify-between">
                        <span className="text-14">
                            <span className="another-grey-color d-inline-block mr-2 fw-500">BIC</span>
                            <span className="fw-600 font-noto-serif">{newData.BIC}</span>
                        </span>
                    </div>
                    {data?.Country ? (
                        <span className="text-14 d-block mb-3">
                            <span className="another-grey-color d-inline-block mr-2 fw-500">
                                {formatMessage(messages.country)}
                            </span>
                            <span className="fw-600">{data.Country}</span>
                        </span>
                    ) : null}
                    {amount !== 0 ? (
                        <span className="text-14 d-block mb-3">
                            <span className="another-grey-color d-inline-block mr-2 fw-500">
                                {formatMessage(messages.amount)}
                            </span>
                            <span className="fw-600">
                                <CustomFormatNumber value={amount} />
                            </span>
                        </span>
                    ) : null}
                </div>

                <div className="w-100 mobile-screen">
                    <span className="text-14 d-block mb-3">
                        <span className="another-grey-color d-block fw-500 mb-1">{formatMessage(messages.accountOwner)}</span>
                        <span className="fw-600">{newData.Owner}</span>
                    </span>
                    <span className="text-14 d-block mb-3">
                        <span className="another-grey-color d-block fw-500 mb-1">
                            IBAN{" "}
                            <ExplanatoryTooltip dataTooltipID="iban" dataTooltipContent={formatMessage(messages.ibanInfoText)} />
                        </span>
                        <span className="fw-600 font-noto-serif">{friendlyFormatIBAN(newData.IBAN)}</span>
                    </span>
                    <div className="flex justify-between">
                        <span className="text-14">
                            <span className="another-grey-color d-block fw-500 mb-1">BIC</span>
                            <span className="fw-600 font-noto-serif">{newData.BIC}</span>
                        </span>
                    </div>
                    {data?.Country ? (
                        <span className="text-14 d-block mb-3">
                            <span className="another-grey-color d-block fw-500 mb-1">{formatMessage(messages.country)}</span>
                            <span className="fw-600">{data.Country}</span>
                        </span>
                    ) : null}
                    {amount !== 0 ? (
                        <span className="text-14 d-block mb-3">
                            <span className="another-grey-color d-block fw-500 mb-1">{formatMessage(messages.amount)}</span>
                            <span className="fw-600">
                                <CustomFormatNumber value={amount} />
                            </span>
                        </span>
                    ) : null}
                    {isVerificationRequired && verificationDesign === "small" && (
                        <div className="d-flex align-center justify-end mt-2">
                            <ExplanatoryTooltip
                                overrideMargin="mr-2"
                                dataTooltipID="verify-sca"
                                dataTooltipContent={formatMessage(messages.completeMobileVerificationMangopay)}
                            />
                            <span className="text-14 fw-500 primary-color" onClick={onVerifyClick}>
                                {formatMessage(messages.verify)}
                            </span>
                        </div>
                    )}
                </div>

                {isVerificationRequired && verificationDesign === "small" && (
                    <div className="desktop-screen">
                        <div className="d-flex align-center">
                            <ExplanatoryTooltip
                                overrideMargin="mr-2"
                                dataTooltipID="verify-sca"
                                dataTooltipContent={formatMessage(messages.completeMobileVerificationMangopay)}
                            />
                            <span className="text-14 fw-500 primary-color" onClick={onVerifyClick}>
                                {formatMessage(messages.verify)}
                            </span>
                        </div>
                    </div>
                )}
            </div>
            {isVerificationRequired && verificationDesign === "large" && (
                <>
                    <div className="desktop-screen">
                        <div className="p-4 border-radius-bottom mangopa-sca-bg-color d-flex justify-between align-start">
                            <span className="text-14 d-block pr-4">
                                <span className="fw-500">{formatMessage(messages.verificationRequired)}:</span>{" "}
                                {formatMessage(messages.completeMobileVerificationMangopay)}
                            </span>
                            <button className="small mb-0" onClick={onVerifyClick}>
                                {formatMessage(messages.verify)}
                            </button>
                        </div>
                    </div>

                    <div className="p-4 border-radius-bottom mangopa-sca-bg-color mobile-screen">
                        <span className="text-14 d-block">
                            <span className="fw-500">{formatMessage(messages.verificationRequired)}:</span>{" "}
                            {formatMessage(messages.completeMobileVerificationMangopay)}
                        </span>
                        <button className="small mw-100 mt-3 mb-0" onClick={onVerifyClick}>
                            {formatMessage(messages.verify)}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default AccountDetailsBlock;
